import { Config } from 'config';

import { forEach, head, last, includes } from 'lodash';

import { invalidateOwner, findByType, deg2rad, Magnitude } from 'gear';

import { Xml } from '../util';
import { Point, Matrix, Cubic, Boundary, Curve, CurveArcLenCursor } from '../geometry';
import { TextChar, TextLayout } from '../figure/text';
import { Process } from './process';



// Some of this objects may end up in separate files later. For the moment, we group here all the
// abstractions that are only used by FitOnPath


// enum VPos: Indicates how the figures are vertically placed along the path
//
//   Above : Figures are placed such that the bottom side of its bounding box aligns with the path
//   Over  : Figures are placed such that the baseline of its bounding box aligns with the path
//   Below : Figures are placed such that the top side of its bounding box aligns with the path

// enum Anchor: Indicates what horizontal grip is used for positioning chars
//   Grip, Left, Middle, Right

// enum _OldFit: Deprcated: this should be replaced by a combination of EFit and some numerical parameters.
//   Fixed_Left, Fixed_CenterTop, Fixed_CenterBottom, Fixed_Right, Stretched, Scaled, Expanded

const CPos = function(value,isAngular) {
  this.value = value;
  this.isAngular = isAngular;
};

const LinealCPos = function(value) {
  return new CPos(value,false);
};
const AngularCPos = function(value) {
  return new CPos(value,true);
};


const ItemInfo = function(pos) {
  this.pos = pos;
  this.loc = null;
  this.locTan = null;
  this.rotation = 0;
  this.scaleX = 1;
  this.scaleY = 1;
};


const Item = function( char, lineIdx, charIdx, ascent, descent, widthwise, vp, anchor ) {
  this.char = char;
  this.lineIdx = lineIdx;
  this.charIdx = charIdx;

  const frame = char._frame; // was char.Shape.Frame ?

  let grip = new Point(0,0);

  const hDir = frame.dir_x;
  const vDir = frame.dir_y;

  // Trace.WriteLine( string.Format("  Wrapping char '{0}' HPos='{1}'", aChar.ID, aChar.HPos) ) ;

  const width = this.width = frame.width;
  const hPos = char._hPos;

  switch( anchor ) {

    case 'Grip':
      this.info = new ItemInfo( hPos );
      grip = widthwise ? char._grip : char._topCenterGrip;
      this.pivot = ( vp === 'Over' ? widthwise ? 'Grip' : 'BaselineCenterGrip'
                   : vp === 'Above' ? 'BottomCenterGrip' : 'TopCenterGrip' );
      break;

    case 'Left':
      const dx = char.bearingLX();
      this.info = new ItemInfo( hPos + dx ) ;
      grip = Point.add( char._grip, Point.sm( hDir, dx ) );

      this.pivot = ( vp === 'Over' ? widthwise ? 'LXBY' : 'BaselineCenterGrip'
                   : vp === 'Above' ? 'LXLY' : 'LXHY' );
      break;

    case 'Middle':
      this.info = new ItemInfo( hPos + Point.distance( char._grip, char._baselineCenterGrip ) );
      grip = widthwise ? char._baselineCenterGrip : char._topCenterGrip;

      this.pivot = ( vp === 'Over' ? 'BaselineCenterGrip'
                   : vp === 'Above' ? 'BottomCenterGrip' : 'TopCenterGrip' );
      break;

    case 'Right':
      const dx_w = char.bearingLX() + width;
      this.info= new ItemInfo( hPos + dx_w );
      grip = Point.add( char._grip, Point.sm( hDir, dx_w ) );

      this.pivot = ( vp === 'Over' ? widthwise ? 'HXBY' : 'BaselineCenterGrip'
                   : vp === 'Above' ? 'HXLY' : 'HXHY' );
      break ;
  }

  this.baseGrip = grip;

  this.topGrip    = Point.add( grip, Point.sm( vDir, ascent  ) );
  this.bottomGrip = Point.add( grip, Point.sm( vDir, descent ) );

  this.hook = ( vp === 'Over' ? this.baseGrip : vp === 'Above' ? this.bottomGrip : this.topGrip );
};

//
// Algorithm:
//  1.Compute the Path Length.
//  2.Compute the 'final' Positions based on the placement options and the initial positions.
//  4.Locate each final Position along the curve and place each grip in it's Location
//
// A "Position", as passed in 'aPos', is an arc-length lineal parameter that indicates
// the distance from the grip to the path origin.
//
// A "Location" is a place along the path that corresponds to a given Position.
// The mapping from Positions to Locations is done by parametrizing the path by arc-length.
// A Location encapsulates a point and a normalized tangent vector.
//
// The "initial" Position for each figure is given by aPos[i]
// The "final" Positions for each figure is given by aPos[i] adjusted according to the variouis
// placement options. In the particular case of "left" aligment, the final positions are directly
// the initials.
//

const _wrapChars = function(layout, lineIdx, vPlacement, anchor ) {
  const items = [];

  const ascent    = layout.ascent(); // [TODO]
  const descent   = layout.descent();
  const widthwise = layout.isWidthwise();

  forEach( layout.textLines()[lineIdx-1]._chars, (char,i) => {

    if( char._outlines ) {
      items.push( new Item( char,
                            lineIdx,
                            i+1,
                            ascent,
                            descent,
                            widthwise,
                            vPlacement,
                            anchor ) );
    }
  });

  return items;
};

const _wrapPos = function(path, pos) {
  if( path.closed ) {
    const arcLen = path.arcLength();
    while( pos > arcLen ) {
      pos -= arcLen;
    }
    while( pos < 0 ) {
      pos += arcLen;
    }
  }
  // Trace.WriteLine( string.Format("    Position={0}",aPos) ) ;
  return pos;
};

const _getAngularPosition = function(path,angle,steadyAngles) {
  const pathBounds = path.bounds;
  const center = pathBounds.center;
  const rayDir = Matrix.rotate(angle).apply_(1,0);

  if( steadyAngles ) {
    const width = pathBounds.width, height = pathBounds.height;
    if( rayDir.y < rayDir.x ) {
      rayDir.y *= height/width;
    }
    else {
      rayDir.x *= width/height;
    }
    rayDir.normalize();
  }

  const rayEndP = Point.add( center, Point.sm( rayDir, Point.distance(pathBounds.lx_ly,pathBounds.hx_hy) ) ); // [TODO]

  return path.segmentIntersectionPos(center,rayEndP);
};

const _arcLenPosValue = function(path,cpos,steadyAngles) {
  const value = cpos.value;
  return cpos.isAngular ? _getAngularPosition(path,value,steadyAngles).arcLen : value;
};

// Computes the final Position for each figure according to the various placement options.
// Each "position" is given by a double value.
const _computePositions = function( textLen,
                                  bearingLX,
                                  path,
                                  fit,
                                  aPathRange_Beg,
                                  aPathRange_End,
                                  aPathPin,
                                  steadyAngles,
                                  textPin,
                                  mirrorX,
                                  items ) {

  let pathLen = path.arcLength(),
      pathRange_Beg = 0,
      pathRange_End = 0,
      pathPin = 0,
      factorX = 1,
      factorY = 1;

  if( path.closed ) {
    pathRange_Beg = _arcLenPosValue(path,aPathRange_Beg,steadyAngles);
    pathRange_End = _arcLenPosValue(path,aPathRange_End,steadyAngles);
    pathPin       = _arcLenPosValue(path,aPathPin      ,steadyAngles);
  }
  else {
    // assert( ! aPathRange_Beg.isAngular );
    // assert( ! aPathRange_End.isAngular );
    // assert( ! aPathPin      .isAngular );

    pathRange_Beg = aPathRange_Beg.value;
    pathRange_End = aPathRange_End.value;
    pathPin       = aPathPin.value;
  }

  let pathRange = pathLen - pathRange_Beg - ( pathLen - pathRange_End ); // pathRange_End - pathRange_Beg

  const textRange = textLen;

  let ratio = pathRange / textRange;

  if( fit === 'Expanded' ) { // [TODO]
    if( ratio < 1 ) {
      const invRatio = 1/ratio;
      path.transform( Matrix.scaleFrom(path.bounds.center,invRatio) );
      pathRange     *= invRatio;
      pathLen       *= invRatio;
      pathRange_Beg *= invRatio;
      pathRange_End *= invRatio;
      pathPin       *= invRatio;
      ratio = 1;
    }
  }

  // [WHY?]
  if( path.closed &&
     ( fit === 'Scaled' || fit === 'Stretched' ) &&
     pathRange === pathLen ) {

    ratio = ( pathRange - ( bearingLX * ratio ) ) / textRange ;
  }

  // Trace.WriteLine( string.Format("  Computing path positions. PathLen={0} PathPin={1} TextLen={2} TextPin={3}",lPathLen, lPathPin, lTextLen, aTextPin) ) ;
  let shift;

  switch( fit ) {
    case 'None':
    case 'Expanded':
      shift = pathPin - textPin;

      if( mirrorX ) {
        const firstItem = head(items);
        const lastItem  = last(items);

        const initialShift = - Point.distance(firstItem.char._grip, firstItem.char._baselineCenterGrip);

        // NOTE: When the Path is open, Corel mirrors horizontally fom the last char's grip and not from the past-the-end grip (right edge of the text)
        let currCPos = lastItem.char._hPos + shift + ( path.closed ? lastItem.char._advanceX : 0 ) + initialShift;

        for( let i = 0, c = items.length, l = c - 1 ; i < c ; ++ i ) {
          const item = items[i];

          const advanceX = ( i < l ? items[i+1].info.pos - item.info.pos : 0 ) ;

          item.info.pos = _wrapPos( path, currCPos );

          currCPos -= advanceX;
        }
      }
      else {
        forEach( items, item => {
          item.info.pos = _wrapPos(path, item.info.pos + shift);
        });
      }
      break;

    case 'Contained':
    case 'ContainedHorizontally':

      if( textRange > pathRange ) {

        factorX = ratio;

        if( fit === 'ContainedHorizontally' ) {
          factorY = Math.sign(ratio) === 1 ? 1.0 : -1.0 ;
        }
        else {
          factorY = ratio ;
        }

        shift = pathRange_Beg - ( bearingLX * factorX );
      }
      else {
        factorX = factorY = 1.0;
        shift = pathPin - textPin - bearingLX;
      }

      forEach( items, item => {
        item.info.pos = _wrapPos(path,( item.info.pos * factorX ) + shift);
      });
      break;

    case 'Stretched':
    case 'Scaled':

      factorX = factorY = ( fit === 'Scaled' ? ratio : ( ratio > 0 ? 1 : -1 ) ) ;

      forEach( items, item => {
        item.info.pos = _wrapPos(path, item.info.pos * ratio );
      });
    break;
  }

  return new Point(factorX,factorY);
};

// Determines the "Location" along the curve for each figure "Position".
// These locations are given by an arc-length parametrization of the path.
// Each "Location" encapsulates a point (on the path) and a tangent vector.
const _createLocations = function( path, fwd, offset, items ) {
  // Trace.WriteLine( string.Format("  Computing locations") ) ;
  const cursor = new CurveArcLenCursor(path);

  for( let k = 0, lastU = 0, kEnd = items.length; k<kEnd; ++k ) {
    const { info } = items[k];
    const U = fwd ? ( info.pos + offset ) : ( path.arcLength() - offset - info.pos );
    const deltaU = U - lastU;
    cursor.move(deltaU);
    info.loc    = cursor.pos();
    info.locTan = cursor.tangent();

    // Trace.WriteLine( string.Format("Offset={0} CPos={1} U={2} P={3}",aOffset,lInfo.Pos,lU,lInfo.Loc.Point) ) ;
    lastU = U;
  }
};

// Places each figure along the path.
// The "place" is given by the Location computed for each figure.
// The following linear transformation is applied to each figure:
//
//   Displacement = Loc[i].Grip - Hook(i)
//     Where Hook(i) = Item[i].Grip + VerticalShift
//       Where VerticalShift = Item[i].Grip - Item[i].GripAlongBottom IF "Above"
//                             Item[i].GripAlongTop - Item[i].Grip    IF "Below"
//                             ZERO                                     OTHERWISE
//
//   Rotation = Angle w.r.t X-axis Loc[i].Tan
//
//   Scaling = NewWidth/Width(i)
//     Where Width(i) = Item[i].BBox.Width
//           NewWidth = PathLen / Count ;
//

const _createResult = function( layout,
                              dontRotate, scale, mirrorX, mirrorY,
                              items ) {

  forEach( items, item => {
    const rotation = dontRotate ? 0 : item.info.locTan.angle();

    if( dontRotate ) {
      scale = new Point( Math.abs(scale.x), Math.abs(scale.y) );
    }

    const pos = item.info.loc.point;

    item.info.rotation = rotation;
    item.info.scaleX   = scale.x;
    item.info.scaleY   = scale.y;

    const charData = item.char._charData;
    const transformers = charData._transformers;
    if( mirrorX || mirrorY ) {
      transformers.push( new TextChar.PivotedMirrorer(item.pivot,mirrorX,mirrorY) );
    }
    if( Math.abs(scale.x) !== 1 || Math.abs(scale.y) !== 1 ) { // [WHY?]
      transformers.push( new TextChar.PivotedScaler(item.pivot,scale.x,scale.y) );
    }
    if( rotation !== 0 ) {
      transformers.push( new TextChar.PivotedRotator(item.pivot,rotation) );
    }

    transformers.push( new TextChar.PinPlacer(item.pivot,pos) );
  });
};

const fitOnPath = function( layout, lineIdx, path,
                          vPlacement, hPlacement, fit,
                          aPathRange_Beg, aPathRange_End, aPathPin,
                          textPin, // This value should be properly scaled
                          dontRotate, fwd, mirrorX, mirrorY, offset, useExtendedTextLength,
                          steadyAngles ) {

  // Trace.WriteLine( string.Format("Fitting TextOnPath") ) ;

  // >> GCurveBoundary lPath = aPath.Copy();

  // It seems we do not need this, the first line baseline is always at the origin for the moment
  // const baselineFrom = layout.textLines()[0]._baseline.P0;
  // if( ! baselineFrom.isZero() ) {
  //  layout.transform( Matrix.translate( Point.minus(baselineFrom) ) ); // This won't work directly
  // }

  const items = _wrapChars( layout, lineIdx, vPlacement, hPlacement, textPin );

  const textLine = layout.textLines()[lineIdx-1];
  const textLength = useExtendedTextLength ? textLine._baseline.arcLength() : textLine.bounds.width; // TODO
  const bearingLX  = useExtendedTextLength ? textLine.bearingLX() : 0 ;

  if( fit === 'Expanded' ) {
    path = path.clone();
  }
  const scale = _computePositions( textLength, bearingLX,
                                 path, // If fit === Expanded, the path can be modified
                                 fit,
                                 aPathRange_Beg, aPathRange_End, aPathPin, steadyAngles,
                                 textPin,
                                 mirrorX,
                                 items );

  _createLocations( path, fwd, offset, items );
  _createResult( layout, dontRotate, scale, mirrorX, mirrorY, items );

  layout.invalidate();
};



const createSingleQuadCircularBezier = function( quad, ccw ) {
  const a = (4/3*(Math.sqrt(2)-1));

  const N = [ 1, 1, a, 0 ];

  const signX = ( quad === 0 || quad === 3 ) ? 1 : -1 ;
  const signY = ( quad === 0 || quad === 1 ) ? 1 : -1 ;

  const p = [];
  for( let i = 0 ; i < 4 ; ++ i ) {
    p.push( new Point( signX * N[ signX === signY ? i     : 3 - i ] ,
                    signY * N[ signX === signY ? 3 - i : i     ] ) );
  }
  if( ! ccw ) {
    p.reverse();
  }
  return new Cubic(p[0],p[1],p[2],p[3]);
};

const buildEllipse = function( center, mainAxisEndP, ratio, startQuad, orientation ) {
  const pieces = [];
  const ccw = orientation === 'CCW' ;
  for( let i = startQuad + (ccw?0:1), c = 0 ; c < 4 ; ++c , i = i + ( ccw ? +1 : -1 ) ) {
    const q = i < 0 ? i + 4 : i ;
    const piece = createSingleQuadCircularBezier(q,ccw);
    pieces.push(piece);
  }

  const curve = Curve.fromNonTiedPieces(pieces,true);

  const majAxis = Point.substract( mainAxisEndP, center );
  const rotAngle = majAxis.angle();
  const xLen     = majAxis.norm();
  const yLen     = xLen * ratio;

  curve.transform( Matrix.concat( Matrix.translate(center), Matrix.rotate(rotAngle), Matrix.scale(xLen,yLen) ) );

  return curve;
};

const createEllipse = function(center, a, b, orientation ) {
  const mainAxisEndP = Point.add(center, a);
  const a_norm2 = a.norm2();
  const b_norm2 = b.norm2();
  const ratio = b_norm2 > 0 && a_norm2 > 0 ? Math.sqrt( b_norm2 / a_norm2 ) : 1;
  return buildEllipse(center, mainAxisEndP, ratio, 0, orientation);
};

const FitOnPathBasedProcess = Process.extend({ typeName: 'FitOnPathBasedProcess',

  Properties: {
    steadyAngles: { type: 'boolean' , def: false , onChange: invalidateOwner, dropDef:true },
  },

  canProcess( figure ) {
    if( ! Config.localFitOnPath ) {
      return false;
    }
    // [TODO] CharsTransform should always be used after the fit on path, but the inverse
    //        is working in the engine and Canvas can not cope with it right now
    const processes = figure._processes;
    const charsTransform = findByType(processes,Process.CharsTransform);
    if( charsTransform && processes.indexOf(charsTransform) < processes.indexOf(this) ) {
      return false;
    }
    return true;
  },

  process( c ) {
    const path = this.path();

    forEach( c.figures, figure => {
      if( figure instanceof TextLayout ) {
        this.applyTo(figure,path);
      }
    });

    c.anchor = path.bounds.center;
  },

  requiresText() { return true; },
  outputsText() { return true; }

});

// ClassicArc
//===============================================================================================

const ClassicArc = Process.ClassicArc = FitOnPathBasedProcess.extend({ typeName: 'ClassicArcProcess',

  type: 'ClassicArc',

  Properties: {
    width      : { type: 'magnitude'  , def: new Magnitude(500,'mm')   , onChange: invalidateOwner },
    height     : { type: 'magnitude'  , def: new Magnitude(100,'mm')   , onChange: invalidateOwner },
    from       : { type: 'angle'   , def: 170   , onChange: invalidateOwner, dropDef:true },
    to         : { type: 'angle'   , def: 10    , onChange: invalidateOwner, dropDef:true },
    dontRotate : { type: 'boolean' , def: false , onChange: invalidateOwner, dropDef:true },
    fit        : { type: 'string'  , def:'Contained', onChange: invalidateOwner, dropDef:true }
  },

  path() {
    // Create ellipse
    const ellipse = createEllipse( new Point(0,0),
                                 new Point( -Math.abs(this._width) / 2, 0 ),
                                 new Point( 0, Math.abs(this._height) / 2 ), 'CW' );
    return ellipse;
  },
  applyTo( layout,ellipse) {
    const from = this._from, to = this._to;
    const upperArc = to < from;
    if( ellipse === undefined ) {
      ellipse = this.path();
    }

    const arcStart = AngularCPos( from );
    const arcEnd   = AngularCPos( to );
    const arcPin   = AngularCPos( (from+to)/2 );

    /* [TODO]
    if( Config.debugFitOnPath ) {
      // const arcStartPos = _arcLenPosValue(ellipse, from);
      // const arcEndPos   = _arcLenPosValue(ellipse, to  );
      // const lDPath = new ShapeFigure(lEllipse.Copy());
      // lDPath.AssignStyle( new GColorPen(GColor.Black), null ) ;
      // const lDArc = new ShapeFigure( new GPolygonalBoundary(false, lArcStartPos.Point, lEllipse.BBox.MXMY, lArcEndPos.Point) ) ;
      // lDArc.AssignStyle( new GColorPen(GColor.Black), null ) ;
      // aOutput.Add(lDPath);
      // aOutput.Add(lDArc);
    }
    */

    fitOnPath( layout, 1,//st line,
               ellipse,
               upperArc ? 'Over' : 'Below',
               'Middle', this._fit,
               arcStart, arcEnd, arcPin,
               head(layout.textLines())._baseline.arcLength() / 2,
               this._dontRotate,
               true, false, false, 0, false, this._steadyAngles );

    layout.invalidate();
    layout.textLines();
  },
  maxHeightForBounds( bounds,textHeight) {
    return _ClassicArc_maxHeightForBounds(bounds,textHeight,this._to);
  }
});
Xml.addFromBuild(ClassicArc);

// In ClassicArc, we need to account for the text rotation to compute the width

// areaHeight = ( 1-sin(to) ) * height + 2 * textHeight
// areaWidth  = cos(to) * ( width + 2 * textHeight )  Worst case, math to complex

const _ClassicArc_maxHeightForBounds = ClassicArc.maxHeightForBounds = function(bounds,textHeight,to) {
  return ( 2 * ( bounds.height - textHeight ) ) / ( 1 - Math.abs(Math.sin(deg2rad(to))) );
};

const _ClassicArc_maxWidthForBounds = ClassicArc.maxWidthForBounds = function(bounds,textHeight,to) {
  return ( bounds.width - 2 * textHeight ) / Math.abs(Math.cos(deg2rad(to))) ;
};

ClassicArc.createForBounds = function(bounds,textHeight,to) {
  if( to === undefined ) {
    to = 20;
  }
  return new ClassicArc({ width : _ClassicArc_maxWidthForBounds(bounds,textHeight,to),
                          height: _ClassicArc_maxHeightForBounds(bounds,textHeight,to),
                          to: to, from: 180-to,
                          fit: 'ContainedHorizontally',
                          steadyAngles: true });
};

// FitEllipse
//===============================================================================================

const FitEllipse = Process.FitEllipse = FitOnPathBasedProcess.extend({ typeName: 'FitEllipseProcess',

  type: 'FitEllipse',

  Properties: {
    height     : { type: 'magnitude'  , def: new Magnitude(0,'mm')  , onChange: invalidateOwner, dropDef:true },
    width      : { type: 'magnitude'  , def: new Magnitude(0,'mm')  , onChange: invalidateOwner, dropDef:true },
    rotation   : { type: 'angle'   , def: 0                         , onChange: invalidateOwner, dropDef:true },
    placement  : { type: 'number'  , def: 90                        , onChange: invalidateOwner, dropDef:true },
    mirror     : { type: 'boolean' , def: false                     , onChange: invalidateOwner, dropDef:true },
    inside     : { type: 'boolean' , def: false                     , onChange: invalidateOwner, dropDef:true },
    dontRotate : { type: 'boolean' , def: false                     , onChange: invalidateOwner, dropDef:true },
    fit        : { type: 'string'  , def: 'None'                    , onChange: invalidateOwner, dropDef:true }
  },

  path() {
    // Create ellipse

    const ellipse = createEllipse( new Point(0,0),
                                 new Point( -Math.abs(this._width) / 2, 0 ),
                                 new Point( 0, Math.abs(this._height) / 2 ), 'CW' );

    if( this._rotation !== 0 ) {
      ellipse.transform( Matrix.rotate( this._rotation ) );
    }
    if( this._mirror ) {
      ellipse.transform( Matrix.scale(-1,1) );
    }

    return ellipse;
  },
  applyTo( layout,ellipse) {
    if( ellipse === undefined ) {
      ellipse = this.path();
    }
    const mirror = this._mirror;
    const vPos = this._inside ? mirror ? 'Over'  : 'Below'
                            : mirror ? 'Below' : 'Over' ;

    const lPathRange_Beg = LinealCPos( 0 );
    const lPathRange_End = LinealCPos( ellipse.arcLength() );
    const lPathPin = AngularCPos( this._placement );
    let lMax = 0;
    for (let lLine = 0; lLine < layout.textLines().length; lLine++) {
      const lLen = layout.textLines()[lLine]._baseline.arcLength();
      lMax = Math.max(lMax,lLen);
    }
    const lTextPin = lMax / 2;
    let lOldY = 0;           
    for (let lLine = 0; lLine < layout.textLines().length; lLine++) {
      const lH = layout.textLines()[lLine]._baseline.P0.y; //bounds.height;
      ellipse.transform(Matrix.translate(0, lH - lOldY));
      lOldY = lH;
      fitOnPath(layout, lLine+1,
                ellipse,
                vPos, 'Middle', this._fit, lPathRange_Beg, lPathRange_End, lPathPin, lTextPin,
                this._dontRotate,
                true, false, false, 0, false, this._steadyAngles );
    }


    layout.invalidate();
    layout.textLines();
  }
});
Xml.addFromBuild(FitEllipse);

// FitOnPath
//===============================================================================================

const FitOnPath = Process.FitOnPath = FitOnPathBasedProcess.extend({ typeName: 'FitOnPathProcess',
  type: 'FitOnPath',
  Properties: {
    vPos             : { type: 'string'     , def: 'Over'                    , onChange: invalidateOwner }, // [TODO]enum
    hPos             : { type: 'string'     , def: 'Fixed_CenterTop'         , onChange: invalidateOwner },
    anchor           : { type: 'string'     , def: 'Middle'                  , onChange: invalidateOwner, dropDef:true },
    offset           : { type: 'magnitude'  , def: new Magnitude(0.0,'mm')   , onChange: invalidateOwner, dropDef:true },
    distanceFromPath : { type: 'magnitude'  , def: new Magnitude(0.0,'mm')   , onChange: invalidateOwner, dropDef:true },
    placeOnOtherSide : { type: 'boolean'    , def: false                     , onChange: invalidateOwner, dropDef:true },
    fwd              : { type: 'boolean'    , def: true                      , onChange: invalidateOwner, dropDef:true },
    dontRotate       : { type: 'boolean'    , def: false                     , onChange: invalidateOwner, dropDef:true },
    overhang         : { type: 'boolean'    , def: false                     , onChange: invalidateOwner, dropDef:true },
    mirrorX          : { type: 'boolean'    , def: false                     , onChange: invalidateOwner, dropDef:true },
    mirrorY          : { type: 'boolean'    , def: false                     , onChange: invalidateOwner, dropDef:true }
  },
  init( c ) {
    this.callParent();
    this._path = c.path || null;
  },
  isEqual( other ) {
    return this.propertiesAreEqual(other) &&
           this._path.isEqual(other._path);
  },
  path( v ) { return v===undefined? this._path :(this._path=v,this.invalidate(),this);},
  setupXml( node ) {
    Xml.setupProperties(node,this);
    this._path.buildXml(node,'Path');
  },
  clone() {
    const c = new FitOnPath(this);
    c.path( this._path.clone() );
    return c;
  },
  applyTo( layout,path) {

    // For FitOnPath to work correctly, the baseline should start at (0.0,0.0)

    if( path === undefined ) {
      path = this._path;
    }
    const baselineLen = head(layout.textLines())._baseline.arcLength();
    const pathLen = path.arcLength();

    const hPlacement = this._hPos;
    const fit = hPlacement === 'Scaled'    ? 'Scaled'
            : hPlacement === 'Stretched' ? 'Stretched'
            : hPlacement === 'Expanded'  ? 'Expanded'
                                         : 'None' ;

    let textPin, pathPin;

    if( path.closed ) {
      textPin = 0.5 * baselineLen ;

      switch( hPlacement ) {
        case 'Fixed_Left':
          pathPin = AngularCPos( 180 ) ;
          break ;

        case 'Fixed_Right':
          pathPin = AngularCPos( 0 ) ;
          break ;

        case 'Fixed_CenterTop':
          pathPin = AngularCPos( 90 ) ;
          break ;

        case 'Fixed_CenterBottom':
          pathPin = AngularCPos( 270 ) ;
          break ;

        default :
          pathPin = AngularCPos( 0 ) ;
          break ;
      }
    }
    else {
      switch( hPlacement ) {
        case 'Fixed_Left':
          textPin = 0 ;
          pathPin = LinealCPos(0) ;
          break ;

        case 'Fixed_Right':
          textPin = baselineLen;
          pathPin = LinealCPos( pathLen ) ;
          break ;

        case 'Fixed_CenterTop':
        case 'Fixed_CenterBottom':
          textPin = 0.5 * baselineLen ;
          pathPin = LinealCPos(0.5 * pathLen ) ;
          break ;

        default :
          textPin = 0 ;
          pathPin = LinealCPos(0);
          break ;
      }
    }

    /* [TODO]
    if( Config.debugFitOnPath ) {
      // const dPath = new ShapeFigure(Path.Copy());
      // lDPath.AssignStyle( new GColorPen(GColor.Black), null ) ;
      // aOutput.Add(lDPath);
    }
    */

    fitOnPath( layout, 1,//st line
               path, this._vPos, this._anchor, fit,
               LinealCPos(0), LinealCPos( pathLen ),
               pathPin, textPin,
               this._dontRotate, this._fwd, this._mirrorX, this._mirrorY,
               this._offset, this._overhang, this._steadyAngles );

    layout.invalidate();
    layout.textLines();
  }
});

// [Patch] We will coerce all enums in future versions
// There was a bug in CorelWorX generating wrong VPos values:
const vPosValues = [ 'Above', 'Over', 'Below' ];

FitOnPath.parseXml = function( node ) {
  const p = Xml.parseProperties(node,Process.FitOnPath);
  p.path = Boundary.fromXml(node,'Path');
  if( ! includes(vPosValues,p.vPos) ){
    p.vPos = 'Over';
  }
  return new FitOnPath(p);
};
Xml.addFromBuild(FitOnPath);



/** WEBPACK FOOTER **
 ** ./src/cdl/process/fit-on-path.js
 **/